<script setup lang="ts">
import { VirtualBaseCarousel } from '#components'
import type { ComponentExposed } from 'vue-component-type-helpers'
import type {
  HouseDetailsAttributes,
  HousePhotoAttributes,
} from 'lc-services/types'
import type { LocalizedString } from 'lc-repository/entities'

const { openedIndex, housePhotos } = defineProps<{
  house: HouseDetailsAttributes
  housePhotos: Array<
    HousePhotoAttributes & { area: LocalizedString; hash: string }
  >
  className: string
  openedIndex: number
  totalCount: number
}>()
const emits = defineEmits<{
  'close-slider': []
  'slide-change': []
}>()

const { isDesktop, isMobile } = useBreakpoint()
const { locale } = useI18n()

const swiperOptions = {
  keyboard: true,
  loop: false,
  preventClicksPropagation: true,
}

const photoSlider = ref<ComponentExposed<
  typeof VirtualBaseCarousel<
    (typeof slides)['value'] extends Array<infer R> ? R : never
  >
> | null>(null)

const slides = computed(() => housePhotos.map(({ url }) => url))
const onSlideChange = (slideIndex: number) => {
  // load the next batch before the last picture is shown so that the slider stays fluid
  if (slideIndex >= housePhotos.length - 2) emits('slide-change')
}

const escFunction = (e: KeyboardEvent) => {
  if (e.key === 'Escape') emits('close-slider')
}

onMounted(() => {
  // need the nextTick, to wait for the VirtualBaseCarousel component to be mounted before you can use the above method
  nextTick(() => {
    photoSlider.value?.slideTo(openedIndex - 1, 0)
  })

  if (isDesktop.value) {
    document.addEventListener('keyup', escFunction, false)
  }
})

onBeforeUnmount(() => {
  if (isDesktop.value) {
    document.removeEventListener('keyup', escFunction, false)
  }
})
</script>

<template>
  <div
    class="overflow-y-none fixed inset-x-0 top-0 z-modal size-full bg-gray-700"
  >
    <button
      class="absolute right-4 top-8 z-fixed flex cursor-pointer items-center justify-center text-white transition duration-300 md:right-10 md:hover:opacity-50"
      type="button"
      @click="$emit('close-slider')"
    >
      <BaseIcon class="mr-4" name="cancel" />
      <span>{{ $t('global.close') }}</span>
    </button>

    <VirtualBaseCarousel
      ref="photoSlider"
      navigation
      :swiper-class="
        'h-screen md:!mx-[20%] md:!pt-16 md:!pb-20'.concat(
          isMobile ? ' !pt-32 !pb-72' : '',
        )
      "
      :class-name="className"
      :base-image-style-object="{
        objectFit: 'contain',
        objectPosition: 'center',
      }"
      :slides="slides"
      :swiper-options="swiperOptions"
      @slide-change-active-index="onSlideChange"
    >
      <template #navigation="{ position }">
        <div
          class="flex flex-col items-center justify-center pb-4 sm:mt-4 sm:flex-row"
        >
          <p class="my-12 min-h-6 text-lg text-white sm:my-0 sm:grow">
            <template v-if="housePhotos[position - 1]?.hash !== 'unassigned'">
              {{ housePhotos[position - 1]?.area?.[locale] }}
            </template>
          </p>
          <div class="flex items-center gap-4">
            <button
              v-if="isMobile"
              :aria-label="$t('global.back')"
              class="swiper--prev"
              type="button"
            >
              <BaseIcon name="chevronLeft" />
            </button>

            <p class="mb-0 text-center text-base text-white">
              {{ position }} / {{ totalCount }}
            </p>
            <button
              v-for="direction in isMobile ? ['next'] : ['prev', 'next']"
              :key="direction"
              :aria-label="
                direction === 'prev' ? $t('global.back') : $t('global.next')
              "
              :class="[`swiper--${direction}`]"
              type="button"
            >
              <BaseIcon
                :name="direction === 'prev' ? 'chevronLeft' : 'chevronRight'"
              />
            </button>
          </div>
        </div>
      </template>
    </VirtualBaseCarousel>
  </div>
</template>

<style scoped>
.swiper--prev,
.swiper--next {
  @apply flex size-10 shrink-0 grow-0 cursor-pointer items-center justify-center rounded-full text-white bg-white/10;
}
@screen sm {
  .swiper--prev,
  .swiper--next {
    @apply size-12 border border-gray-700/10 bg-white px-2 text-gray-700 disabled:bg-gray-200 disabled:text-gray-400;
  }
}
</style>
